import { useState } from "react";
import { Controller, useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { Eye, EyeOff } from "lucide-react";
import { FormField } from "@/components/form/FormField";
import JsonViewer from "@/components/shared/json-viewer";
import { useUpdateStep } from "@/hooks/api/mutations/workflows.mutations";
import type { WorkflowStep } from "@/types/workflow.types";
import { MCPToolSelect } from "./mcp-tool-select";
import { HighlightedCodeEditor } from "./highlighted-code-editor";

const schema = z.object({
  toolId: z.string().min(1, "Tool required"),
  inputMapping: z.string().refine((v) => {
    try { JSON.parse(v); return true; } catch { return false; }
  }, { message: "Invalid JSON" }),
  outputMapping: z.string().optional().refine((v) => {
    if (!v?.trim()) return true;
    try { JSON.parse(v); return true; } catch { return false; }
  }, { message: "Invalid JSON" }),
  timeoutSeconds: z.number().int().min(1).max(300).optional(),
  maxRetries: z.number().int().min(0).max(3),
});

type FormValues = z.infer<typeof schema>;

function safeStringify(val: unknown): string {
  if (typeof val === "string") return val;
  if (val === null || val === undefined) return "";
  try { return JSON.stringify(val, null, 2); } catch { return ""; }
}

function toFormValues(config: Record<string, unknown>): FormValues {
  return {
    toolId: String(config.tool_id ?? ""),
    inputMapping: safeStringify(config.input_mapping) || "{}",
    outputMapping: safeStringify(config.output_mapping) || "",
    timeoutSeconds: typeof config.timeout_seconds === "number" ? config.timeout_seconds : undefined,
    maxRetries: typeof config.max_retries === "number" ? config.max_retries : 0,
  };
}

function toPayload(values: FormValues): Record<string, unknown> {
  let inputMappingParsed: unknown = {};
  let outputMappingParsed: unknown = null;
  try { inputMappingParsed = JSON.parse(values.inputMapping); } catch { /* schema enforced valid */ }
  if (values.outputMapping?.trim()) {
    try { outputMappingParsed = JSON.parse(values.outputMapping); } catch { /* schema enforced */ }
  }
  return {
    tool_id: values.toolId,
    input_mapping: inputMappingParsed,
    output_mapping: outputMappingParsed,
    timeout_seconds: values.timeoutSeconds ?? null,
    max_retries: values.maxRetries,
  };
}

interface McpToolCallConfigFormProps {
  step: WorkflowStep;
  workflowId: string;
  onSuccess?: () => void;
}

export function McpToolCallConfigForm({ step, workflowId, onSuccess }: McpToolCallConfigFormProps) {
  const t = useTranslations("workflows");
  const updateStep = useUpdateStep();
  const [previewInput, setPreviewInput] = useState(false);

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(step.config),
  });

  const { control, handleSubmit, register, watch, formState: { errors, isSubmitting } } = methods;

  const inputMapping = watch("inputMapping");
  const timeoutSeconds = watch("timeoutSeconds");

  let parsedInputPreview: unknown = null;
  try { parsedInputPreview = JSON.parse(inputMapping); } catch { /* invalid JSON during typing */ }

  const onSubmit = handleSubmit(async (values) => {
    await updateStep.mutateAsync({ workflowId, stepId: step.id, config: toPayload(values) });
    onSuccess?.();
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        <MCPToolSelect
          name="toolId"
          label={t("drawer.field.tool_id")}
          required
        />

        <FormField
          label={t("drawer.field.input_mapping")}
          hint="JSON template — use {{stepAlias.field}} for context bindings"
          error={errors.inputMapping?.message}
        >
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span />
              <button
                type="button"
                onClick={() => setPreviewInput((p) => !p)}
                className="flex items-center gap-1 text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
              >
                {previewInput ? <EyeOff className="size-3" /> : <Eye className="size-3" />}
                {previewInput ? "Edit" : "Preview"}
              </button>
            </div>
            {previewInput && parsedInputPreview !== null ? (
              <div className="rounded-lg border border-[var(--border)] bg-[var(--muted)] p-3">
                <JsonViewer value={parsedInputPreview} defaultExpandDepth={2} />
              </div>
            ) : (
              <Controller
                control={control}
                name="inputMapping"
                render={({ field }) => (
                  <HighlightedCodeEditor
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.value)}
                    onBlur={field.onBlur}
                    language="json"
                    rows={5}
                    placeholder={'{\n  "query": "{{previousStep.output}}"\n}'}
                    minHeight={132}
                    invalid={!!errors.inputMapping}
                  />
                )}
              />
            )}
          </div>
        </FormField>

        <FormField
          label={t("drawer.field.output_mapping")}
          hint="Optional — map tool response fields to workflow context"
          error={errors.outputMapping?.message}
        >
          <Controller
            control={control}
            name="outputMapping"
            render={({ field }) => (
              <HighlightedCodeEditor
                value={field.value ?? ""}
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={field.onBlur}
                language="json"
                rows={3}
                placeholder={'{"result": "$.data.value"}'}
                minHeight={90}
                invalid={!!errors.outputMapping}
              />
            )}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label={t("drawer.field.timeout_seconds")}
            hint={timeoutSeconds ? `${timeoutSeconds}s` : "Default 30s"}
            error={errors.timeoutSeconds?.message}
          >
            <input
              type="number"
              min={1}
              max={300}
              {...register("timeoutSeconds", { valueAsNumber: true })}
              placeholder="30"
              className="h-9 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 text-sm focus:border-[var(--ring)] focus:outline-none"
            />
          </FormField>

          <FormField label={t("drawer.field.max_retries")} error={errors.maxRetries?.message}>
            <select
              {...register("maxRetries", { valueAsNumber: true })}
              className="h-9 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 text-sm focus:border-[var(--ring)] focus:outline-none"
            >
              {[0, 1, 2, 3].map((n) => (
                <option key={n} value={n}>{n === 0 ? "No retry" : `${n} retr${n === 1 ? "y" : "ies"}`}</option>
              ))}
            </select>
          </FormField>
        </div>

        {updateStep.isError && (
          <p className="text-xs text-[var(--destructive)]">{t("drawer.save_error")}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || updateStep.isPending}
          className="h-9 w-full rounded-lg bg-[var(--primary)] text-sm font-medium text-[var(--primary-foreground)] hover:opacity-90 disabled:opacity-60"
        >
          {isSubmitting || updateStep.isPending ? t("drawer.saving") : t("drawer.save")}
        </button>
      </form>
    </FormProvider>
  );
}

export default McpToolCallConfigForm;
